import React, {useState, useCallback, useMemo} from "react";
import {useMap} from "react-map-gl";
import Supercluster from "supercluster";
import ClusterMarker from "../MapMarkers/ClusterMarker";
import ClusterContext from "./ClusterContext";
import {parseAndValidateLngLat} from "../../utils/geo";

//Clustering has to be done without any wrappers, as the cluster component directly accesses its children
//This component renders all the clustering markers
// Optionally accept a custom cluster marker component to alter styling
// (e.g., red warning clusters for traffic). Defaults to the standard ClusterMarker.
const ClusterHandler = ({children, onClickCluster, clusterMarkerComponent}) => {
  const [markerClusters, setMarkerClusters] = useState([]);
  const mapRef = useMap()?.current; // MapRef from react-map-gl v7

  //Uses useCallback and useMemo to prevent unnecessary rerenders when the registered markers change
  const unRegisterMarkers = useCallback((removeMarkers) => {
    setMarkerClusters((currentItems) => {
      //Filter out any markers passed into the call
      return currentItems.filter(
        (registeredMarkers) =>
          !removeMarkers.some(
            (removeMarker) => removeMarker === registeredMarkers
          )
      );
    });
  }, []);

  const registerMarkers = useCallback((markers) => {
    setMarkerClusters((currentItems) => {
      //Register only markers that have not already been registered
      return [
        ...currentItems,
        ...markers.filter(
          (newMarker) =>
            !currentItems.some((currentMarker) => currentMarker === newMarker)
        ),
      ];
    });
  }, []);

  const registrationHandler = useMemo(
    () => ({ registerMarkers, unRegisterMarkers }),
    [registerMarkers, unRegisterMarkers]
  );

  // Build GeoJSON points from registered Marker elements
  const points = useMemo(() => {
    return markerClusters
      .map((markerEl) => {
        const {longitude, latitude} = markerEl.props || {};
        const {lat, lng, valid} = parseAndValidateLngLat(longitude, latitude);
        if (!valid) return null;
        return {
          type: "Feature",
          properties: {markerEl},
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
        };
      })
      .filter(Boolean);
  }, [markerClusters]);

  // Derive bounds and zoom from the Map instance
  // Always read latest bounds/zoom from the map on each render
  const map = mapRef?.getMap?.();
  const bounds = map
    ? [
      map.getBounds().getWest(),
      map.getBounds().getSouth(),
      map.getBounds().getEast(),
      map.getBounds().getNorth(),
    ]
    : null;
  const zoom = map ? map.getZoom() : 0;

  const index = useMemo(() => {
    const sc = new Supercluster({radius: 40, maxZoom: 20});
    sc.load(points);
    return sc;
  }, [points]);

  const clusters = useMemo(() => {
    if (!index || !bounds) return [];
    // supercluster expects integer zooms
    const z = Math.max(0, Math.floor(zoom));
    return index.getClusters(bounds, z);
  }, [index, bounds, zoom]);

  return (
    <ClusterContext.Provider value={registrationHandler}>
      {clusters.map((feature, idx) => {
        const [rawLongitude, rawLatitude] = feature.geometry.coordinates;
        // Normalize and validate coordinates using the shared helper
        const {lat, lng, valid} = parseAndValidateLngLat(
          rawLongitude,
          rawLatitude
        );
        if (!valid) return null;
        const props = feature.properties;
        if (props.cluster) {
          const clusterId = props.cluster_id;
          const pointCount = props.point_count;
          const ClusterComp = clusterMarkerComponent || ClusterMarker;
          return (
            <ClusterComp
              key={`cluster-${clusterId}-${idx}`}
              longitude={lng}
              latitude={lat}
              pointCount={pointCount}
              onClick={() => {
                const expansionZoom = index.getClusterExpansionZoom(
                  clusterId
                );
                onClickCluster({
                  zoom: expansionZoom,
                  longitude: lng,
                  latitude: lat,
                });
              }}
            />
          );
        }
        // Render the original marker element for a single point
        const orig = props.markerEl;
        return React.cloneElement(orig, {
          key: orig.key || `marker-${lng}-${lat}-${idx}`,
        });
      })}
      {children}
    </ClusterContext.Provider>
  );
};

export default ClusterHandler;
